import React, { useCallback, useEffect, useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';

import { DateField } from '../shared/DateField';

/**
 * Swaps Lifecycle — post-ACQUIRE contract management for IRS + CDS/CDX
 * (CDS E2E 2026-08-06; IRS E2E open item 5a: marks and unwinds were
 * API-only with no UI entry point).
 *
 * Lists ACTIVE swap contracts for a portfolio (GET
 * /api/derivatives/irs/contracts, /api/derivatives/cds/contracts) with
 * per-row actions:
 *   Mark    -> POST /api/derivatives/irs/mtm  {contractId, netPv, asOfDate}
 *              POST /api/derivatives/cds/mtm  {contractId, currentSpreadBps | netPv, asOfDate}
 *              (CDS marks are engine-computed from the spread via
 *              CDSPricingService — RPV01 at the new spread)
 *   Unwind  -> POST /api/derivatives/{irs|cds}/unwind {contractId, terminationValue, asOfDate}
 * Every action books a REAL posted lifecycle trade through
 * DerivativePostingSpi — bi-temporal supersede, cash settlement on unwind.
 */

const panel: React.CSSProperties = {
  border: '1px solid rgba(148,163,184,0.18)', background: 'rgba(15,23,42,0.55)',
  borderRadius: 10, padding: '16px 20px', marginBottom: 16,
};
const label: React.CSSProperties = {
  color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
};
const th: React.CSSProperties = {
  ...label, textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid rgba(148,163,184,0.18)',
};
const td: React.CSSProperties = {
  color: '#e2e8f0', fontSize: 13, textAlign: 'right', padding: '8px 10px',
  borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap',
};
const input: React.CSSProperties = {
  background: '#0f172a', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 8,
  color: '#e2e8f0', padding: '8px 12px', fontSize: 13, width: '100%',
};
const btn = (tone: string): React.CSSProperties => ({
  background: tone === 'amber' ? 'rgba(245,158,11,0.15)' : tone === 'red' ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
  border: `1px solid ${tone === 'amber' ? 'rgba(245,158,11,0.4)' : tone === 'red' ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.4)'}`,
  color: tone === 'amber' ? '#fbbf24' : tone === 'red' ? '#f87171' : '#60a5fa',
  borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6,
});

const fmt = (v: any, digits = 2) =>
  v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });

type SwapRow = {
  key: string;
  kind: 'IRS' | 'CDS' | 'CDX' | 'TRS' | 'CCS' | 'SWAPTION' | 'FX';
  contractId: number;
  name: string;
  detail: string;
  direction: string;
  notional: number;
  entry: string;
  spreadBps: number | null;   // CDS current spread (mark prefill)
  refPrice: number | null;    // TRS initial/reference price (mark prefill)
  mark: number | null;        // currentNpv (IRS) / mtmValue (CDS) / currentMtm (TRS)
  lastValuation: string | null;
  maturity: string | null;
  // FX (V467): near-leg settle is a lifecycle action on the same contract,
  // and an NDF fixes rather than unwinds.
  fxType?: 'FORWARD' | 'SWAP' | 'NDF';
  fxStatus?: string;
};

type ModalState = { kind: 'mark' | 'unwind' | 'settleNear' | 'fix'; row: SwapRow } | null;

const SwapsLifecycleScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [rows, setRows] = useState<SwapRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [value, setValue] = useState('');       // NPV (IRS mark) / termination value (unwind)
  const [spread, setSpread] = useState('');     // CDS mark: spread bps
  const [price, setPrice] = useState('');       // TRS mark: reference-asset price / CCS+FX: rate
  const [quoteRate, setQuoteRate] = useState(''); // FX mark: quote-ccy discount rate (decimal)
  const [asOf, setAsOf] = useState<string>(getTenantToday());
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    axios.get('/api/dimensions/portfolios')
      .then(r => setPortfolios(Array.isArray(r.data) ? r.data : (r.data?.content || [])))
      .catch(() => setPortfolios([]));
  }, []);

  const refresh = useCallback(async (pid: string) => {
    if (!pid) { setRows([]); return; }
    try {
      const [irs, cds, trs, ccs, swpt, fx] = await Promise.all([
        axios.get(`/api/derivatives/irs/contracts?portfolioId=${pid}`).catch(() => ({ data: [] })),
        axios.get(`/api/derivatives/cds/contracts?portfolioId=${pid}`).catch(() => ({ data: [] })),
        axios.get(`/api/derivatives/trs/contracts?portfolioId=${pid}`).catch(() => ({ data: [] })),
        axios.get(`/api/derivatives/ccs/contracts?portfolioId=${pid}`).catch(() => ({ data: [] })),
        axios.get(`/api/derivatives/swaption/contracts?portfolioId=${pid}`).catch(() => ({ data: [] })),
        axios.get(`/api/derivatives/fx/trades?portfolioId=${pid}`).catch(() => ({ data: [] })),
      ]);
      const irsRows: SwapRow[] = (irs.data || []).map((c: any) => ({
        key: `irs-${c.id}`,
        kind: 'IRS' as const,
        contractId: c.id,
        name: `IRS ${c.swapType || ''} ${c.floatingIndex || ''}`.trim(),
        detail: `contract #${c.id}`,
        direction: c.fixedPayerReceiver === 'PAYER' ? 'PAY FIXED' : 'RECEIVE FIXED',
        notional: Number(c.notionalAmount),
        entry: c.fixedRate != null ? `${Number(c.fixedRate).toFixed(2)}% fixed` : '—',
        spreadBps: null,
        refPrice: null,
        mark: c.currentNpv != null ? Number(c.currentNpv) : null,
        lastValuation: c.lastValuationDate || null,
        maturity: c.maturityDate || null,
      }));
      const cdsRows: SwapRow[] = (cds.data || []).map((c: any) => ({
        key: `cds-${c.id}`,
        kind: (c.contractType === 'INDEX' ? 'CDX' : 'CDS') as 'CDS' | 'CDX',
        contractId: c.id,
        name: c.referenceEntityName || `CDS #${c.id}`,
        detail: `contract #${c.id}`,
        direction: c.direction === 'SELL_PROTECTION' ? 'SELL PROTECTION' : 'BUY PROTECTION',
        notional: Number(c.notionalAmount),
        entry: `${fmt(c.fixedRateBps, 0)}bps run @ ${fmt(c.currentSpreadBps, 0)}bps`,
        spreadBps: c.currentSpreadBps != null ? Number(c.currentSpreadBps) : null,
        refPrice: null,
        mark: c.mtmValue != null ? Number(c.mtmValue) : null,
        lastValuation: c.lastValuationDate || null,
        maturity: c.maturityDate || null,
      }));
      const trsRows: SwapRow[] = (trs.data || []).map((c: any) => ({
        key: `trs-${c.id}`,
        kind: 'TRS' as const,
        contractId: c.id,
        name: `TRS ${c.referenceAssetDescription || c.referenceAssetCusip || ('#' + c.id)}`,
        detail: `contract #${c.id}`,
        direction: c.totalReturnPayerReceiver === 'PAYER' ? 'PAY TOTAL RETURN' : 'RECEIVE TOTAL RETURN',
        notional: Number(c.notionalAmount),
        entry: `${c.financingRateIndex || 'FIN'}(${fmt(c.financingIndexRate)}%) + ${fmt(c.financingSpreadBps, 0)}bps @ P0 ${fmt(c.initialPrice)}`,
        spreadBps: null,
        refPrice: c.initialPrice != null ? Number(c.initialPrice) : null,
        mark: c.currentMtm != null ? Number(c.currentMtm) : null,
        lastValuation: c.lastValuationDate || null,
        maturity: c.maturityDate || null,
      }));
      const ccsRows: SwapRow[] = (ccs.data || []).map((c: any) => ({
        key: `ccs-${c.id}`,
        kind: 'CCS' as const,
        contractId: c.id,
        name: `CCS ${c.notionalCurrency || 'USD'}/${c.xccyCounterCurrency || '?'}`,
        detail: `contract #${c.id}`,
        direction: c.fixedPayerReceiver === 'PAYER'
          ? `PAY ${c.notionalCurrency || 'BASE'}` : `RECEIVE ${c.notionalCurrency || 'BASE'}`,
        notional: Number(c.notionalAmount),
        entry: `FX0 ${fmt(c.xccyInitialFxRate, 4)} · last ${fmt(c.xccyLastFxRate, 4)}`,
        spreadBps: null,
        refPrice: c.xccyLastFxRate != null ? Number(c.xccyLastFxRate) : null,
        mark: c.currentNpv != null ? Number(c.currentNpv) : null,
        lastValuation: c.lastValuationDate || null,
        maturity: c.maturityDate || null,
      }));
      const swptRows: SwapRow[] = (swpt.data || []).map((c: any) => ({
        key: `swpt-${c.id}`,
        kind: 'SWAPTION' as const,
        contractId: c.id,
        name: `Swaption ${(c.optionDirection || '').replace('_', ' ')}`,
        detail: `contract #${c.id}`,
        direction: (c.optionDirection || '').replace('_', ' '),
        notional: Number(c.notionalAmount),
        entry: `strike ${fmt(c.strikeRate)}% · prem ${fmt(c.premiumAmount, 0)} · exp ${c.expirationDate ? String(c.expirationDate).slice(0, 10) : '—'}`,
        spreadBps: null,
        refPrice: null,
        mark: c.currentMtm != null ? Number(c.currentMtm) : null,
        lastValuation: c.lastValuationDate || null,
        maturity: c.expirationDate || null,
      }));
      // FX (V467): SPOT never appears — it is a settled currency exchange,
      // not an open contract with a mark.
      const fxRows: SwapRow[] = (fx.data || [])
        .filter((c: any) => c.fxType && c.fxType !== 'SPOT')
        .map((c: any) => {
          const isSwap = c.fxType === 'SWAP';
          const nearSettled = c.status === 'NEAR_SETTLED';
          return {
            key: `fx-${c.id}`,
            kind: 'FX' as const,
            contractId: c.id,
            name: `FX ${c.fxType} ${c.currencyPair || ''}`.trim(),
            detail: `fx_trade #${c.id}${isSwap ? (nearSettled ? ' · near settled' : ' · near pending') : ''}`,
            direction: `${c.buySellBase || 'BUY'} ${c.baseCurrency || 'BASE'}`,
            notional: Number(c.baseAmount),
            entry: isSwap
              ? `near ${fmt(c.nearLegRate, 4)} @ ${c.nearLegDate ? String(c.nearLegDate).slice(0, 10) : '—'} · far ${fmt(c.farLegRate, 4)} @ ${c.farLegDate ? String(c.farLegDate).slice(0, 10) : '—'}`
              : c.fxType === 'NDF'
                ? `contract ${fmt(c.forwardRate, 4)} · fix ${c.fixingDate ? String(c.fixingDate).slice(0, 10) : '—'} (${c.fixingSource || 'n/a'})`
                : `outright ${fmt(c.forwardRate, 4)} @ ${c.forwardDate ? String(c.forwardDate).slice(0, 10) : '—'}`,
            spreadBps: null,
            refPrice: c.lastMarkRate != null
              ? Number(c.lastMarkRate)
              : (isSwap ? (c.farLegRate != null ? Number(c.farLegRate) : null)
                        : (c.forwardRate != null ? Number(c.forwardRate) : null)),
            mark: c.currentMtm != null ? Number(c.currentMtm) : null,
            lastValuation: c.lastMtmDate || null,
            maturity: (isSwap ? c.farLegDate : c.forwardDate) || null,
            fxType: c.fxType,
            fxStatus: c.status,
          };
        });
      setRows([...irsRows, ...cdsRows, ...trsRows, ...ccsRows, ...swptRows, ...fxRows]);
    } catch { setRows([]); }
  }, []);

  useEffect(() => { refresh(portfolioId); }, [portfolioId, refresh]);

  const runAction = async (fn: () => Promise<any>, doneLabel: string) => {
    setBusy(true); setMsg(null);
    try {
      const res = await fn();
      const ok = res?.data?.success !== false;
      const already = res?.data?.alreadyMarked || res?.data?.alreadyUnwound;
      setMsg({ ok, text: already ? 'Already recorded for this contract/date (idempotent)' : (res?.data?.message || doneLabel) });
      await refresh(portfolioId);
      setModal(null); setValue(''); setSpread(''); setPrice(''); setQuoteRate('');
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const submitModal = () => {
    if (!modal) return;
    const { kind, row } = modal;
    // Explicit map (FX E2E 2026-08-08): the old chain fell THROUGH to the CDS
    // endpoint for any unrecognized kind, so a new lane silently posted its
    // marks to the wrong silo instead of failing.
    const BASE_BY_KIND: Record<SwapRow['kind'], string> = {
      IRS: '/api/derivatives/irs',
      TRS: '/api/derivatives/trs',
      CCS: '/api/derivatives/ccs',
      SWAPTION: '/api/derivatives/swaption',
      CDS: '/api/derivatives/cds',
      CDX: '/api/derivatives/cds',
      FX: '/api/derivatives/fx',
    };
    const base = BASE_BY_KIND[row.kind];
    if (!base) {
      setMsg({ ok: false, text: `No lifecycle endpoint wired for ${row.kind}` });
      return;
    }
    if (kind === 'mark') {
      if (row.kind === 'IRS' || row.kind === 'SWAPTION') {
        runAction(() => axios.post(`${base}/mtm`, {
          contractId: row.contractId, netPv: Number(value), asOfDate: asOf,
        }), `Marked — NPV ${fmt(Number(value))}`);
      } else if (row.kind === 'TRS') {
        runAction(() => axios.post(`${base}/mtm`, {
          contractId: row.contractId, currentPrice: Number(price), asOfDate: asOf,
        }), `Marked @ price ${price}`);
      } else if (row.kind === 'CCS') {
        runAction(() => axios.post(`${base}/mtm`, {
          contractId: row.contractId, currentFxRate: Number(price), asOfDate: asOf,
        }), `Marked @ FX ${price}`);
      } else if (row.kind === 'FX') {
        runAction(() => axios.post(`${base}/mtm`, {
          fxTradeId: row.contractId, currentRate: Number(price),
          quoteRate: quoteRate === '' ? null : Number(quoteRate), asOfDate: asOf,
        }), `Marked @ rate ${price}`);
      } else {
        runAction(() => axios.post(`${base}/mtm`, {
          contractId: row.contractId, currentSpreadBps: Number(spread), asOfDate: asOf,
        }), `Marked @ ${spread}bps`);
      }
    } else if (kind === 'settleNear') {
      runAction(() => axios.post(`${base}/settle-near`, {
        fxTradeId: row.contractId, currentFarRate: Number(price),
        quoteRate: quoteRate === '' ? null : Number(quoteRate), asOfDate: asOf,
      }), 'Near leg settled — physical exchange booked, MTM re-anchored to the far leg');
    } else if (kind === 'fix') {
      runAction(() => axios.post(`${base}/fix`, {
        fxTradeId: row.contractId, fixingRate: Number(price), asOfDate: asOf,
      }), `NDF fixed @ ${price} — cash settled`);
    } else {
      runAction(() => axios.post(`${base}/unwind`, {
        ...(row.kind === 'FX' ? { fxTradeId: row.contractId } : { contractId: row.contractId }),
        terminationValue: value === '' ? null : Number(value),
        asOfDate: asOf,
      }), 'Contract unwound');
    }
  };

  const markValid = modal?.kind === 'mark'
    ? ((modal.row.kind === 'IRS' || modal.row.kind === 'SWAPTION') ? value !== ''
        : (modal.row.kind === 'TRS' || modal.row.kind === 'CCS' || modal.row.kind === 'FX') ? price !== ''
        : spread !== '')
    : (modal?.kind === 'settleNear' || modal?.kind === 'fix') ? price !== ''
    : true;

  return (
    <div style={{ padding: '8px 4px', color: '#e2e8f0', marginTop: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Swaps Lifecycle</div>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>
          Mark, settle and unwind open IRS / CDS / CDX / TRS / CCS / Swaption / FX contracts — every
          action posts a lifecycle trade to IBOR (CDS marks engine-computed from the credit spread;
          FX from the current forward rate; FX swaps settle their near leg here, NDFs fix)
        </div>
      </div>

      <div style={panel}>
        <div style={label}>Portfolio</div>
        <select style={{ ...input, maxWidth: 420, marginTop: 6 }} value={portfolioId}
          onChange={e => setPortfolioId(e.target.value)}>
          <option value="">Select portfolio…</option>
          {portfolios.map((p: any) => (
            <option key={p.id} value={p.id}>{p.portfolioName}</option>
          ))}
        </select>
      </div>

      {msg && (
        <div style={{ ...panel, borderColor: msg.ok ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)',
          color: msg.ok ? '#4ade80' : '#f87171', fontSize: 13 }}>
          {msg.text}
        </div>
      )}

      <div style={{ ...panel, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Contract</th>
              <th style={{ ...th, textAlign: 'left' }}>Direction</th>
              <th style={th}>Notional</th>
              <th style={{ ...th, textAlign: 'left' }}>Entry</th>
              <th style={th}>Mark (MTM)</th>
              <th style={th}>Last Valuation</th>
              <th style={th}>Maturity</th>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#64748b', padding: 24 }}>
                {portfolioId ? 'No open swap contracts in this portfolio' : 'Select a portfolio'}
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.key}>
                <td style={{ ...td, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  <div style={{ color: '#94a3b8', fontSize: 11 }}>{r.kind} · {r.detail}</div>
                </td>
                <td style={{ ...td, textAlign: 'left', fontSize: 12 }}>{r.direction}</td>
                <td style={td}>{fmt(r.notional, 0)}</td>
                <td style={{ ...td, textAlign: 'left', fontSize: 12 }}>{r.entry}</td>
                <td style={{ ...td, color: (r.mark ?? 0) >= 0 ? '#4ade80' : '#f87171' }}>${fmt(r.mark)}</td>
                <td style={td}>{r.lastValuation ? String(r.lastValuation).slice(0, 10) : '—'}</td>
                <td style={td}>{r.maturity ? String(r.maturity).slice(0, 10) : '—'}</td>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24 }}>
                  <button style={btn('blue')} disabled={busy}
                    onClick={() => {
                      setModal({ kind: 'mark', row: r });
                      setValue(r.mark != null ? String(r.mark) : '');
                      setSpread(r.spreadBps != null ? String(r.spreadBps) : '');
                      setPrice(r.refPrice != null ? String(r.refPrice) : '');
                    }}>
                    Mark
                  </button>
                  {r.kind === 'FX' && r.fxType === 'SWAP' && r.fxStatus === 'OPEN' && (
                    <button style={btn('amber')} disabled={busy}
                      onClick={() => {
                        setModal({ kind: 'settleNear', row: r });
                        setPrice(r.refPrice != null ? String(r.refPrice) : '');
                      }}>
                      Settle Near
                    </button>
                  )}
                  {r.kind === 'FX' && r.fxType === 'NDF' && (
                    <button style={btn('amber')} disabled={busy}
                      onClick={() => {
                        setModal({ kind: 'fix', row: r });
                        setPrice(r.refPrice != null ? String(r.refPrice) : '');
                      }}>
                      Fix
                    </button>
                  )}
                  <button style={btn('red')} disabled={busy}
                    onClick={() => {
                      setModal({ kind: 'unwind', row: r });
                      setValue(r.mark != null ? String(r.mark) : '');
                    }}>
                    Unwind
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !busy && setModal(null)}>
          <div style={{ ...panel, width: 440, background: '#0f172a', marginBottom: 0 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
              {modal.kind === 'mark' ? 'Mark to Market'
                : modal.kind === 'settleNear' ? 'Settle Near Leg (Physical Exchange)'
                : modal.kind === 'fix' ? 'NDF Fixing (Cash Settlement)'
                : 'Unwind (Full Termination)'} — {modal.row.name}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 14 }}>
              {modal.kind === 'mark' && modal.row.kind === 'IRS' &&
                'Posts a real IRS/MTM trade; position MV rolls to the new NPV with bi-temporal supersede. No cash moves.'}
              {modal.kind === 'mark' && modal.row.kind === 'SWAPTION' &&
                'Explicit option value (no vol pricer yet — disclosed). Posts a real MTM trade; unrealized = MV − premium cost basis. No cash moves.'}
              {modal.kind === 'mark' && modal.row.kind === 'TRS' &&
                'MTM engine-computed from the reference price: unrealized TR + accrued coupon − accrued financing since effective. Posts a real MTM trade rolling position MV. No cash moves.'}
              {modal.kind === 'mark' && modal.row.kind === 'CCS' &&
                'First-order FX-delta NPV (rate legs at par, disclosed): counterNotional × (FX0 − FXnow), signed by direction. Posts a real MTM trade rolling position MV. No cash moves.'}
              {modal.kind === 'mark' && (modal.row.kind === 'CDS' || modal.row.kind === 'CDX') &&
                'The MTM level is engine-computed from the spread (RPV01 at the new spread); posts a real MTM trade rolling position MV. No cash moves.'}
              {modal.kind === 'mark' && modal.row.kind === 'FX' &&
                'MTM engine-computed from the current forward (or NDF) rate, discounted at the quote-currency rate when supplied. Posts a real MTM trade rolling position MV. No cash moves.'}
              {modal.kind === 'settleNear' &&
                'Books the near-leg PHYSICAL exchange at the contracted near rate (currency position + settlement cash) and re-anchors the mark to the far leg only. The contract stays open.'}
              {modal.kind === 'fix' &&
                'Cash-settles the NDF at the official fixing: settlement = notional x (fixing - contract) / fixing, signed to your side. No principal is ever exchanged. Flattens the position and marks the contract SETTLED.'}
              {modal.kind === 'unwind' &&
                'Posts a DISPOSE trade: settles the termination value to cash, realizes P&L vs cost basis, flattens the position and marks the contract TERMINATED. One-shot per contract.'}
            </div>
            {modal.kind === 'mark' && (modal.row.kind === 'IRS' || modal.row.kind === 'SWAPTION') && (
              <div style={{ marginBottom: 12 }}>
                <div style={label}>New NPV* ($, signed — positive = asset to us)</div>
                <input style={{ ...input, marginTop: 6 }} type="number" value={value}
                  onChange={e => setValue(e.target.value)} autoFocus />
              </div>
            )}
            {modal.kind === 'mark' && modal.row.kind === 'TRS' && (
              <div style={{ marginBottom: 12 }}>
                <div style={label}>Reference Asset Price* (clean, e.g. 101.25)</div>
                <input style={{ ...input, marginTop: 6 }} type="number" value={price}
                  onChange={e => setPrice(e.target.value)} autoFocus />
              </div>
            )}
            {modal.kind === 'mark' && modal.row.kind === 'CCS' && (
              <div style={{ marginBottom: 12 }}>
                <div style={label}>Current FX Rate* (base per 1 counter, e.g. 1.1150)</div>
                <input style={{ ...input, marginTop: 6 }} type="number" step="0.0001" value={price}
                  onChange={e => setPrice(e.target.value)} autoFocus />
              </div>
            )}
            {((modal.kind === 'mark' && modal.row.kind === 'FX') || modal.kind === 'settleNear' || modal.kind === 'fix') && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <div style={label}>
                    {modal.kind === 'fix'
                      ? 'Fixing Rate* (official fix, e.g. 5.3200)'
                      : modal.kind === 'settleNear'
                        ? 'Current Far-Leg Forward Rate* (marks the far leg at settle)'
                        : `Current ${modal.row.fxType === 'NDF' ? 'NDF' : 'Forward'} Rate* (quote per 1 base, e.g. 1.1150)`}
                  </div>
                  <input style={{ ...input, marginTop: 6 }} type="number" step="0.0001" value={price}
                    onChange={e => setPrice(e.target.value)} autoFocus />
                </div>
                {modal.kind !== 'fix' && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={label}>Quote-Currency Rate (decimal, e.g. 0.048 — blank = undiscounted)</div>
                    <input style={{ ...input, marginTop: 6 }} type="number" step="0.0001" value={quoteRate}
                      onChange={e => setQuoteRate(e.target.value)} />
                  </div>
                )}
              </>
            )}
            {modal.kind === 'mark' && (modal.row.kind === 'CDS' || modal.row.kind === 'CDX') && (
              <div style={{ marginBottom: 12 }}>
                <div style={label}>Current Market Spread* (bps)</div>
                <input style={{ ...input, marginTop: 6 }} type="number" value={spread}
                  onChange={e => setSpread(e.target.value)} autoFocus />
              </div>
            )}
            {modal.kind === 'unwind' && (
              <div style={{ marginBottom: 12 }}>
                <div style={label}>Termination Value ($, signed — positive = we receive; blank = current mark)</div>
                <input style={{ ...input, marginTop: 6 }} type="number" value={value}
                  onChange={e => setValue(e.target.value)} autoFocus />
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <div style={label}>As-Of Date</div>
              <DateField
                style={{ width: '100%', marginTop: 6 }}
                value={asOf ?? ''}
                onChange={iso => setAsOf(iso)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={{ ...btn('blue'), background: 'transparent', borderColor: 'rgba(148,163,184,0.3)', color: '#94a3b8' }}
                disabled={busy} onClick={() => setModal(null)}>Cancel</button>
              <button style={btn(modal.kind === 'unwind' ? 'red' : 'amber')}
                disabled={busy || !markValid}
                onClick={submitModal}>
                {busy ? 'Posting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwapsLifecycleScreen;
